import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUserStore } from '../store/user';

function Dashboard() {
  const { usageStats, fetchUsageStats, isLoadingStats } = useUserStore();
  const dashboardTools = [
    {
      id: 'text-to-speech',
      icon: '🗣️',
      title: 'Text to Speech',
      description:
        'Convert written text into realistic speech with voice/model selection, playback, and history.',
      bestFor: 'Narration, voice-overs, and AI-assisted content reading.',
      highlights: ['AI voice selection', 'Playback + history', 'Language/model control'],
      path: '/text-to-speech',
    },
    {
      id: 'realtime-speech-to-text',
      icon: '🎧',
      title: 'Realtime Speech to Text',
      description:
        'Transcribe your microphone in real time so spoken words appear instantly while you speak.',
      bestFor: 'Live notes, interviews, and speaking practice.',
      highlights: ['Live microphone input', 'Streaming partial transcripts', 'Finalize full transcript'],
      path: '/realtime-speech-to-text',
    },
    {
      id: 'speech-to-text',
      icon: '📝',
      title: 'Speech to Text',
      description: 'Upload recorded audio files and convert speech into editable text within seconds.',
      bestFor: 'Recorded meetings, podcasts, and lecture transcription.',
      highlights: ['File upload', 'Automatic transcription', 'Fast text extraction'],
      path: '/speech-to-text',
    },
    {
      id: 'voice-changer',
      icon: '🎛️',
      title: 'Voice Changer',
      description:
        'Transform uploaded voice clips with pitch, speed, and gain controls to create new voice styles.',
      bestFor: 'Character voices, demos, and creative sound design.',
      highlights: ['Pitch shift', 'Speed control', 'Gain control'],
      path: '/voice-changer',
    },
    {
      id: 'text-to-sound-effects',
      icon: '💥',
      title: 'Text to Sound Effects',
      description:
        'Generate custom sound effects from written prompts and download them as production-ready audio.',
      bestFor: 'Video edits, games, social content, and prototypes.',
      highlights: ['Prompt-based generation', 'Adjustable duration', 'Downloadable WAV output'],
      path: '/text-to-sound-effects',
    },
    {
      id: 'voice-isolator',
      icon: '🧼',
      title: 'Voice Isolator',
      description:
        'Extract and enhance voice frequencies from noisy recordings for cleaner vocal audio.',
      bestFor: 'Noisy recordings, call cleanup, and podcast prep.',
      highlights: ['WAV/MP3 upload', 'Noise-focused cleanup', 'Voice enhancement'],
      path: '/voice-isolator',
    },
    {
      id: 'voice-cleaner',
      icon: '✨',
      title: 'Voice Cleaner',
      description:
        'Use Text to Speech with advanced cleanup enabled for hum/static reduction and normalized narration output.',
      bestFor: 'Podcasts, ads, and polished client-ready narration.',
      highlights: ['One-click cleaner mode', 'AI text generation + speech', 'Cleaner final audio output'],
      path: '/voice-cleaner',
    },
    {
      id: 'voice-cloning',
      icon: '🎭',
      title: 'Voice Cloning',
      description:
        'Create custom AI voices from your samples and reuse them in future speech generation.',
      bestFor: 'Brand voices, character consistency, and personalized TTS.',
      highlights: ['Sample upload', 'Model training', 'Reusable cloned voices'],
      path: '/voice-cloning',
    },
  ];

  useEffect(() => {
    fetchUsageStats();
  }, [fetchUsageStats]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Dashboard</h1>
        <p className="text-gray-400">
          Launch any workspace below to generate, transcribe, transform, or clean audio with dedicated tools.
        </p>
      </div>

      {/* Usage Stats - Quick Overview */}
      {!isLoadingStats && usageStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="card text-center">
            <p className="text-gray-400 text-sm mb-1">Daily Usage</p>
            <p className="text-2xl font-bold">
              {usageStats.daily_usage} / {usageStats.daily_limit}
            </p>
          </div>
          <div className="card text-center">
            <p className="text-gray-400 text-sm mb-1">Monthly Usage</p>
            <p className="text-2xl font-bold">
              {usageStats.monthly_usage} / {usageStats.monthly_limit}
            </p>
          </div>
          <div className="card text-center">
            <p className="text-gray-400 text-sm mb-1">Remaining Daily</p>
            <p className="text-2xl font-bold text-green-400">
              {usageStats.remaining_daily}
            </p>
          </div>
          <div className="card text-center">
            <p className="text-gray-400 text-sm mb-1">Remaining Monthly</p>
            <p className="text-2xl font-bold text-green-400">
              {usageStats.remaining_monthly}
            </p>
          </div>
        </div>
      )}

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-2">Your Audio Tools</h2>
        <p className="text-gray-400 mb-4">
          Each tool opens in its own focused page with controls specific to that workflow.
        </p>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {dashboardTools.map((tool) => (
            <Link
              key={tool.id}
              to={tool.path}
              className="card h-full text-left transition-colors hover:border-purple-500/50"
            >
              <div className="w-12 h-12 rounded-lg bg-purple-500/15 text-2xl flex items-center justify-center mb-3">
                {tool.icon}
              </div>
              <h3 className="text-lg font-semibold mb-2">{tool.title}</h3>
              <p className="text-sm text-gray-400 mb-3">{tool.description}</p>
              <ul className="space-y-1 mb-3">
                {tool.highlights.map((highlight) => (
                  <li key={highlight} className="text-xs text-gray-300 flex items-start">
                    <span className="text-purple-400 mr-2">•</span>
                    {highlight}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-purple-300">Best for: {tool.bestFor}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="grid lg:grid-cols-3 gap-4">
        <div className="card">
          <h3 className="text-lg font-semibold mb-3">Quick Start Flow</h3>
          <ol className="space-y-2 text-sm text-gray-300 list-decimal list-inside">
            <li>Select the tool for your task</li>
            <li>Upload/record/generate your source input</li>
            <li>Preview, refine settings, then download</li>
          </ol>
        </div>
        <div className="card">
          <h3 className="text-lg font-semibold mb-3">Realtime STT Tips</h3>
          <ul className="space-y-2 text-sm text-gray-300">
            <li>• Use Chrome/Edge for stable MediaRecorder support</li>
            <li>• Speak continuously for clearer streaming chunks</li>
            <li>• Click Stop to finalize full transcript text</li>
          </ul>
        </div>
        <div className="card">
          <h3 className="text-lg font-semibold mb-3">Supported Inputs</h3>
          <ul className="space-y-2 text-sm text-gray-300">
            <li>• STT: WAV/FLAC/OGG/MP3/WebM</li>
            <li>• Voice Changer: WAV/MP3</li>
            <li>• Voice Isolator: WAV/MP3</li>
          </ul>
        </div>
      </section>
    </div>
  );
}

export default Dashboard;
